import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebookF,
  faTwitter,
  faLinkedinIn,
  faYoutube,
  faInstagram
} from '@fortawesome/free-brands-svg-icons';
import {
  faPhone,
  faEnvelope,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import footerLogo from '../assets/logo_white.png';
import { useLanguage } from '../i18n/LanguageContext';

const FooterContainer = styled.footer`
  position: relative;
  padding: 110px 20px 50px;
  color: #fff;
  background: linear-gradient(180deg, #0b1221 0%, #0f172a 45%, #121c31 100%);
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 90px 18px 46px;
  }

  @media (max-width: 480px) {
    padding: 74px 16px 40px;
  }
`;

const FooterGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 18% 22%, rgba(12, 77, 162, 0.3), transparent 55%),
    radial-gradient(circle at 82% 18%, rgba(0, 168, 232, 0.24), transparent 52%),
    radial-gradient(circle at center, rgba(15, 23, 42, 0.85), transparent 70%);
  opacity: 0.65;
`;

const FooterContent = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.3fr repeat(3, 1fr);
  gap: 40px;
  z-index: 1;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 640px) {
    gap: 16px;
  }
`;

const FooterLogo = styled.div`
  margin-bottom: 14px;
`;

const FooterLogoImage = styled.img`
  height: 80px;
  width: auto;

  @media (max-width: 480px) {
    height: 56px;
  }
`;

const FooterText = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.8;

  @media (max-width: 768px) {
    line-height: 1.7;
  }
`;

const FooterTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.78);
`;

const Divider = styled.span`
  display: block;
  width: 42px;
  height: 2px;
  background: linear-gradient(135deg, rgba(12, 77, 162, 1), rgba(88, 156, 237, 1));
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FooterLink = styled.li`
  a {
    color: rgba(255, 255, 255, 0.66);
    text-decoration: none;
    font-weight: 500;
    font-size: 0.95rem;
    transition: color 0.2s ease, transform 0.2s ease;

    &:hover {
      color: #fff;
      transform: translateX(4px);
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 10px;

  @media (max-width: 480px) {
    flex-wrap: wrap;
  }
`;

const SocialIcon = styled.a`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-4px);
  background: rgba(12, 77, 162, 0.9);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  gap: 12px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.95rem;
  line-height: 1.6;

  @media (max-width: 640px) {
    align-items: flex-start;
  }

  @media (max-width: 480px) {
    font-size: 0.92rem;
  }
`;

const ContactIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(12, 77, 162, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);

  @media (max-width: 640px) {
    flex-shrink: 0;
  }
`;

const FooterBottom = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 60px auto 0;
  padding-top: 30px;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: rgba(255, 255, 255, 0.54);
  font-size: 0.9rem;
  text-align: center;
  z-index: 1;

  @media (max-width: 768px) {
    margin-top: 48px;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const Footer = () => {
  const { t } = useLanguage();
  const quickLinks = t('footer.quickLinks.items');
  const serviceLinks = t('footer.services.items');
  return (
    <FooterContainer>
      <FooterGlow />
      <FooterContent>
        <FooterColumn>
          <FooterLogo>
            <FooterLogoImage src={footerLogo} alt="Pride Trans" />
          </FooterLogo>
          <FooterText>
            {t('footer.intro')}
          </FooterText>
          <SocialLinks>
            <SocialIcon href="#">
              <FontAwesomeIcon icon={faFacebookF} />
            </SocialIcon>
            <SocialIcon href="#">
              <FontAwesomeIcon icon={faTwitter} />
            </SocialIcon>
            <SocialIcon href="#">
              <FontAwesomeIcon icon={faLinkedinIn} />
            </SocialIcon>
            <SocialIcon href="#">
              <FontAwesomeIcon icon={faYoutube} />
            </SocialIcon>
            <SocialIcon href="#">
              <FontAwesomeIcon icon={faInstagram} />
            </SocialIcon>
          </SocialLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterTitle>{t('footer.quickLinks.title')}</FooterTitle>
          <Divider />
          <FooterLinks>
            {quickLinks.map((item) => (
              <FooterLink key={item.to}><Link to={item.to}>{item.label}</Link></FooterLink>
            ))}
          </FooterLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterTitle>{t('footer.services.title')}</FooterTitle>
          <Divider />
          <FooterLinks>
            {serviceLinks.map((item) => (
              <FooterLink key={item.to}><Link to={item.to}>{item.label}</Link></FooterLink>
            ))}
          </FooterLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterTitle>{t('footer.contact.title')}</FooterTitle>
          <Divider />
          <ContactInfo>
            <ContactIcon>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
            </ContactIcon>
            <div>{t('footer.contact.address')}</div>
          </ContactInfo>
          <ContactInfo>
            <ContactIcon>
              <FontAwesomeIcon icon={faPhone} />
            </ContactIcon>
            <div>+212-661859009</div>
          </ContactInfo>
          <ContactInfo>
            <ContactIcon>
              <FontAwesomeIcon icon={faPhone} />
            </ContactIcon>
            <div>+212-700765376</div>
          </ContactInfo>
          <ContactInfo>
            <ContactIcon>
              <FontAwesomeIcon icon={faEnvelope} />
            </ContactIcon>
            <div>pridetrans3@gmail.com</div>
          </ContactInfo>
        </FooterColumn>
      </FooterContent>
      <FooterBottom>
        <div>&copy; {new Date().getFullYear()} PRIDE TRANS. {t('footer.bottom.rights')}</div>
        <div>{t('footer.bottom.terms')} &nbsp;|&nbsp; {t('footer.bottom.privacy')}</div>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;